import { useState, useEffect, useCallback } from 'react';
import type {
  AccountPayable,
  AccountPayableState,
  AccountPayableActions,
  AccountPayableFilters,
  PaymentFormData,
  AccountPayableFormData
} from '@/types/accounts-payable.types';
import { accountsPayableService } from '@/services/accountsPayableService';
import { toast } from 'sonner';

const initialFilters: AccountPayableFilters = {
  status: 'all',
  supplier: '',
  location: '',
  dateFrom: '',
  dateTo: '',
  dueDateFrom: '',
  dueDateTo: '',
  sortBy: 'createdAt',
  sortOrder: 'desc'
};

export const useAccountsPayable = (): AccountPayableState & AccountPayableActions => {
  const [state, setState] = useState<AccountPayableState>({
    accounts: [],
    currentAccount: null,
    stats: null,
    filters: initialFilters,
    loading: false,
    error: null,
    meta: {
      total: 0,
      hasMore: false,
      topSuppliers: []
    }
  });

  const fetchAccounts = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const response = await accountsPayableService.fetchAccounts(state.filters);

      setState(prev => ({
        ...prev,
        loading: false,
        accounts: response.data,
        stats: response.meta.stats,
        meta: {
          total: response.meta.total,
          hasMore: response.meta.hasMore,
          topSuppliers: response.meta.topSuppliers
        }
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al cargar cuentas por pagar';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      toast.error(errorMessage);
    }
  }, [state.filters]);

  const fetchAccount = useCallback(async (accountId: string): Promise<AccountPayable | null> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const response = await accountsPayableService.fetchAccount(accountId);

      setState(prev => ({
        ...prev,
        loading: false,
        currentAccount: response.data
      }));

      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al cargar cuenta por pagar';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      toast.error(errorMessage);
      return null;
    }
  }, []);

  const createAccount = useCallback(async (accountData: AccountPayableFormData): Promise<AccountPayable | null> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const response = await accountsPayableService.createAccount(accountData);

      setState(prev => ({
        ...prev,
        loading: false,
        accounts: [...prev.accounts, response.data]
      }));

      toast.success(response.message || 'Cuenta por pagar creada exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al crear cuenta por pagar';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      toast.error(errorMessage);
      return null;
    }
  }, []);

  const registerPayment = useCallback(async (paymentData: PaymentFormData): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const response = await accountsPayableService.registerPayment(paymentData);

      // Actualizar la cuenta en el estado local
      setState(prev => ({
        ...prev,
        loading: false,
        accounts: prev.accounts.map(account =>
          account.id === response.updatedAccountPayable.id
            ? response.updatedAccountPayable
            : account
        ),
        currentAccount: prev.currentAccount?.id === response.updatedAccountPayable.id
          ? response.updatedAccountPayable
          : prev.currentAccount
      }));

      toast.success(response.message || 'Pago registrado exitosamente');
      
      // Refrescar datos para actualizar estadísticas
      await fetchAccounts();
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al registrar pago';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      toast.error(errorMessage);
      return false;
    }
  }, [fetchAccounts]);

  const updateFilters = useCallback((newFilters: Partial<AccountPayableFilters>) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...newFilters }
    }));
  }, []);

  const getOverdueAccounts = useCallback((): AccountPayable[] => {
    const now = new Date();
    return state.accounts.filter(account =>
      account.dueDate &&
      new Date(account.dueDate) < now &&
      account.pendingAmount > 0
    );
  }, [state.accounts]);

  const getAccountsBySupplier = useCallback((supplierId: string): AccountPayable[] => {
    return state.accounts.filter(account => account.supplierId === supplierId);
  }, [state.accounts]);

  // Efecto para cargar cuentas cuando cambian los filtros
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return {
    // Estado
    accounts: state.accounts,
    currentAccount: state.currentAccount,
    stats: state.stats,
    filters: state.filters,
    loading: state.loading,
    error: state.error,
    meta: state.meta,

    // Acciones
    fetchAccounts,
    fetchAccount,
    createAccount,
    registerPayment,
    updateFilters,
    getOverdueAccounts,
    getAccountsBySupplier
  };
};